/**
 * API 服务层：与 jpp-backend Controller 路径及 DTO 对齐，统一 POST + JSON body。
 * 读取仍使用 query 来合并同参数的在途请求；HTTP POST 不代表 mutation。
 * 手动加载的列表/详情由调用方在写入后刷新，不同时订阅自动失效刷新。
 */
import { api } from '@store/api';
import type {
  BaseUuidReq,
  ItemQueryReq,
  ItemDetailVO,
  PageInfo,
  ItemSearchVO,
  SearchConditionVO,
  BaseCartItemReq,
  CartItemUpdateReq,
  CartVO,
  OrderCreateFromItemReq,
  OrderConfirmReq,
  OrderQueryReq,
  OrderVO,
  LoginReq,
  RegisterReq,
  VerifyReq,
  GuestReq,
  GuestVO,
  TokenShareConfirmReq,
  TokenShareConfirmVO,
  LoginVO,
  UserVO,
  AddressVO,
  BaseAddressReq,
  ItemCreateReq,
  ItemUpdateReq,
  ItemInfoUpdateReq,
  ItemStatusUpdateReq,
  CategoryCreateReq,
  CategoryUpdateReq,
  OrderLogisticsUpdateReq,
  IndexCategoryVO,
  CategoryVO,
  CountryVO,
  ProvinceVO,
  ProvinceQueryReq,
  ItemCommentUserQueryReq,
  ItemCommentQueryReq,
  ItemCommentUserCreateReq,
  ItemCommentCreateReq,
  ItemCommentUserVO,
  ItemCommentManageVO,
  CouponPreviewReq,
  CouponBatchCreateReq,
  CouponBatchCreateVO,
  CouponBatchDeleteReq,
  CouponBatchDeleteVO,
  CouponQueryReq,
  CouponCodeVO,
  OrderCouponVO,
  TrafficMarkReq,
  TrafficMarkQueryReq,
  TrafficMarkVO,
  TrafficLinkCreateReq,
  TrafficLinkVO,
  AnnouncementVO,
  AnnouncementUpdateReq,
} from '#types/api';

// ---------- 公告：公开展示 / 管理配置 ----------
export const announcementApi = api.injectEndpoints({
  endpoints: (builder) => ({
    announcementGet: builder.query<AnnouncementVO, void>({
      query: () => ({ url: '/c/announcement/get', method: 'POST', body: {} }),
      providesTags: ['Announcement'],
    }),
    announcementManageGet: builder.query<AnnouncementVO, void>({
      query: () => ({ url: '/m/announcement/get', method: 'POST', body: {} }),
      providesTags: ['Announcement'],
    }),
    announcementUpdate: builder.mutation<AnnouncementVO, AnnouncementUpdateReq>({
      query: (body) => ({ url: '/m/announcement/update', method: 'POST', body }),
      invalidatesTags: (_result, error) => error ? [] : ['Announcement'],
    }),
  }),
});

export const {
  useAnnouncementGetQuery, useAnnouncementManageGetQuery, useAnnouncementUpdateMutation,
} = announcementApi;

// ---------- 引流统计：公开上报 / 管理 ----------
export const trafficApi = api.injectEndpoints({
  endpoints: (builder) => ({
    trafficVisit: builder.mutation<void, TrafficMarkReq>({
      query: (body) => ({ url: '/c/traffic/visit', method: 'POST', body, timeout: 5000, keepalive: true }),
    }),
    trafficMarkCreate: builder.mutation<{ uuid: string }, TrafficMarkReq>({
      query: (body) => ({ url: '/m/traffic/mark/create', method: 'POST', body }),
    }),
    trafficMarkList: builder.query<PageInfo<TrafficMarkVO>, TrafficMarkQueryReq>({
      query: (body) => ({ url: '/m/traffic/mark/list', method: 'POST', body }),
    }),
    trafficLinkCreate: builder.mutation<TrafficLinkVO, TrafficLinkCreateReq>({
      query: (body) => ({ url: '/m/traffic/link/create', method: 'POST', body }),
      invalidatesTags: (_result, error, body) => error ? [] : [{ type: 'TrafficLink', id: body.mark }],
    }),
    trafficLinkList: builder.query<TrafficLinkVO[], TrafficMarkReq>({
      query: (body) => ({ url: '/m/traffic/link/list', method: 'POST', body }),
      providesTags: (_result, _error, body) => [{ type: 'TrafficLink', id: body.mark }],
    }),
  }),
});

export const {
  useTrafficVisitMutation, useTrafficMarkCreateMutation, useTrafficMarkListQuery,
  useTrafficLinkCreateMutation, useTrafficLinkListQuery,
} = trafficApi;

// ---------- C 端：首页 / 分类 ----------
export const retailApi = api.injectEndpoints({
  endpoints: (builder) => ({
    indexCategory: builder.query<IndexCategoryVO[], void>({
      query: () => ({ url: '/u/index/category', method: 'POST', body: {} }),
      providesTags: ['Category'],
    }),
    indexSearchCondition: builder.query<SearchConditionVO[], void>({
      query: () => ({ url: '/u/index/search_condition', method: 'POST', body: {} }),
      providesTags: ['Category'],
    }),
    itemQuery: builder.query<ItemSearchVO, ItemQueryReq>({
      query: (body) => ({ url: '/u/item/query', method: 'POST', body: body ?? {} }),
      keepUnusedDataFor: 0,
    }),
    countryGet: builder.query<CountryVO[], void>({
      query: () => ({ url: '/c/country/get', method: 'POST', body: {} }),
      transformResponse: (response: unknown): CountryVO[] => {
        if (Array.isArray(response)) return response as CountryVO[];
        const obj = response as Record<string, unknown> | null;
        if (obj?.data != null && Array.isArray(obj.data)) return obj.data as CountryVO[];
        return [];
      },
    }),
    provinceGet: builder.query<ProvinceVO[], ProvinceQueryReq>({
      query: (body) => ({ url: '/c/province/get', method: 'POST', body: body ?? {} }),
      transformResponse: (response: unknown): ProvinceVO[] => {
        if (Array.isArray(response)) return response as ProvinceVO[];
        const obj = response as Record<string, unknown> | null;
        if (obj?.data != null && Array.isArray(obj.data)) return obj.data as ProvinceVO[];
        return [];
      },
    }),
    itemDetail: builder.query<ItemDetailVO, BaseUuidReq>({
      query: (body) => ({ url: '/u/item/detail', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
  }),
});

// ---------- C 端：购物车 ----------
export const cartApi = api.injectEndpoints({
  endpoints: (builder) => ({
    cartGet: builder.query<CartVO, void>({
      query: () => ({ url: '/u/cart/get', method: 'POST', body: {} }),
      providesTags: ['Cart'],
    }),
    cartAdd: builder.mutation<void, BaseCartItemReq>({
      query: (body) => ({ url: '/u/cart/add', method: 'POST', body }),
      invalidatesTags: ['Cart'],
    }),
    cartUpdate: builder.mutation<void, CartItemUpdateReq>({
      query: (body) => ({ url: '/u/cart/update', method: 'POST', body }),
      invalidatesTags: ['Cart'],
    }),
    cartDeleteItem: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/u/cart/delete/item', method: 'POST', body }),
      invalidatesTags: ['Cart'],
    }),
    cartDeleteSoldOut: builder.mutation<void, void>({
      query: () => ({ url: '/u/cart/delete/soldout', method: 'POST', body: {} }),
      invalidatesTags: ['Cart'],
    }),
  }),
});

// ---------- C 端：订单 ----------
export const orderApi = api.injectEndpoints({
  endpoints: (builder) => ({
    orderCreateCart: builder.mutation<string, void>({
      query: () => ({ url: '/u/order/create/cart', method: 'POST', body: {} }),
      invalidatesTags: ['Order', 'Cart'],
    }),
    orderCreateFromItem: builder.mutation<string, OrderCreateFromItemReq>({
      query: (body) => ({ url: '/u/order/create/item', method: 'POST', body }),
      invalidatesTags: ['Order', 'Cart'],
    }),
    orderConfirm: builder.mutation<string, OrderConfirmReq>({
      query: (body) => ({ url: '/u/order/confirm', method: 'POST', body }),
      invalidatesTags: ['Order'],
    }),
    couponPreview: builder.mutation<OrderCouponVO, CouponPreviewReq>({
      query: (body) => ({ url: '/u/order/coupon/preview', method: 'POST', body }),
    }),
    orderList: builder.query<PageInfo<OrderVO>, OrderQueryReq>({
      query: (body) => ({ url: '/u/order/list', method: 'POST', body: body ?? {} }),
      keepUnusedDataFor: 0,
    }),
    orderDetail: builder.query<OrderVO, BaseUuidReq>({
      query: (body) => ({ url: '/u/order/detail', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
    orderCancel: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/u/order/cancel', method: 'POST', body }),
      invalidatesTags: (_result, _error, { uuid }) => [{ type: 'Order', id: uuid }, 'Order'],
    }),
  }),
});

// ---------- C 端：用户 / 认证 ----------
export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    userLogin: builder.mutation<LoginVO, LoginReq>({
      query: (body) => ({ url: '/u/user/login', method: 'POST', body }),
    }),
    userRegister: builder.mutation<UserVO, RegisterReq>({
      query: (body) => ({ url: '/u/user/register', method: 'POST', body }),
    }),
    userVerify: builder.mutation<LoginVO, VerifyReq>({
      query: (body) => ({ url: '/u/user/verify', method: 'POST', body: body ?? {} }),
      invalidatesTags: ['User'],
    }),
    userGet: builder.query<UserVO, void>({
      query: () => ({ url: '/u/user/get', method: 'POST', body: {} }),
      providesTags: ['User'],
    }),
    userLogout: builder.mutation<void, void>({
      query: () => ({ url: '/u/user/logout', method: 'POST', body: {} }),
      invalidatesTags: ['User'],
    }),
    userGuest: builder.mutation<GuestVO, GuestReq | void>({
      // 项目约定：请求 body 必须有，无参数时传空对象 {}
      // credentials: 'omit' 与 curl 行为一致，避免跨域时 CORS 对带凭证请求的严格要求导致失败
      query: (body) => ({
        url: '/u/user/guest',
        method: 'POST',
        body: body && typeof body === 'object' ? body : {},
        credentials: 'omit',
      }),
    }),
    userTokenShareConfirm: builder.mutation<TokenShareConfirmVO, TokenShareConfirmReq>({
      query: (body) => ({ url: '/u/user/token/share/confirm', method: 'POST', body }),
      transformResponse: (response: unknown): TokenShareConfirmVO => {
        if (typeof response === 'string') return { token: response };
        return response as TokenShareConfirmVO;
      },
    }),
  }),
});

// ---------- C 端：地址 ----------
export const addressApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addressList: builder.query<AddressVO[], void>({
      query: () => ({ url: '/u/address/list', method: 'POST', body: {} }),
      providesTags: ['User'],
    }),
    addressAdd: builder.mutation<void, BaseAddressReq>({
      query: (body) => ({ url: '/u/address/create', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    addressUpdate: builder.mutation<void, AddressVO>({
      query: (body) => ({ url: '/u/address/update', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    addressDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/u/address/delete', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
  }),
});

// ---------- 管理端：分类 ----------
export const categoryManageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    categoryAdd: builder.mutation<{ uuid: string }, CategoryCreateReq>({
      query: (body) => ({ url: '/m/category/create', method: 'POST', body }),
      invalidatesTags: ['Category', 'Product'],
    }),
    categoryGetAll: builder.query<CategoryVO[], void>({
      query: () => ({ url: '/m/category/get/all', method: 'POST', body: {} }),
      providesTags: ['Category'],
      transformResponse: (response: unknown): CategoryVO[] => {
        if (Array.isArray(response)) return response;
        const r = response as { data?: CategoryVO[] };
        return Array.isArray(r?.data) ? r.data : [];
      },
    }),
    categoryUpdate: builder.mutation<void, CategoryUpdateReq>({
      query: (body) => ({ url: '/m/category/update', method: 'POST', body }),
      invalidatesTags: ['Category', 'Product'],
    }),
    categoryDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/m/category/delete', method: 'POST', body }),
      invalidatesTags: ['Category', 'Product'],
    }),
  }),
});

// ---------- 管理端：商品 ----------
export const itemManageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    itemList: builder.query<PageInfo<ItemDetailVO>, ItemQueryReq>({
      query: (body) => ({ url: '/m/item/list', method: 'POST', body: body ?? {} }),
      providesTags: [{ type: 'Product', id: 'LIST' }],
      transformResponse: (response: unknown, _meta, _arg) => {
        const r = response as Record<string, unknown>;
        return (r && 'data' in r && r.data != null ? r.data : r) as PageInfo<ItemDetailVO>;
      },
    }),
    itemCreate: builder.mutation<void, ItemCreateReq>({
      query: (body) => ({ url: '/m/item/create', method: 'POST', body }),
      invalidatesTags: ['Product'],
    }),
    itemUpdate: builder.mutation<void, ItemUpdateReq>({
      query: (body) => ({ url: '/m/item/update', method: 'POST', body }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Product', id: arg.uuid }, { type: 'Product', id: 'LIST' }],
    }),
    itemComplianceUpdate: builder.mutation<void, ItemInfoUpdateReq>({
      query: (body) => ({ url: '/m/item/update/compliance', method: 'POST', body }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Product', id: arg.uuid }, { type: 'Product', id: 'LIST' }],
    }),
    itemStatusUpdate: builder.mutation<void, ItemStatusUpdateReq>({
      query: (body) => ({ url: '/m/item/status/update', method: 'POST', body }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Product', id: arg.uuid }, { type: 'Product', id: 'LIST' }],
    }),
    itemDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/m/item/delete', method: 'POST', body }),
      invalidatesTags: ['Product'],
    }),
    itemManageDetail: builder.query<ItemDetailVO, BaseUuidReq>({
      query: (body) => ({ url: '/m/item/detail', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
    itemComplianceDetail: builder.query<ItemDetailVO, BaseUuidReq>({
      query: (body) => ({ url: '/m/item/detail/compliance', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
  }),
});

// ---------- 管理端：订单物流 ----------
export const orderManageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    orderManageDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/m/order/delete', method: 'POST', body }),
    }),
    orderLogisticsUpdate: builder.mutation<void, OrderLogisticsUpdateReq>({
      query: (body) => ({ url: '/m/order/logistics/update', method: 'POST', body }),
      invalidatesTags: ['Order'],
    }),
    orderManageList: builder.query<PageInfo<OrderVO>, OrderQueryReq>({
      query: (body) => ({ url: '/m/order/list', method: 'POST', body: body ?? {} }),
      keepUnusedDataFor: 0,
    }),
    orderManageDetail: builder.query<OrderVO, BaseUuidReq>({
      query: (body) => ({ url: '/m/order/detail', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
  }),
});

// ---------- 管理端：优惠码 ----------
export const couponManageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    couponBatchCreate: builder.mutation<CouponBatchCreateVO, CouponBatchCreateReq>({
      query: (body) => ({ url: '/m/coupon/batch/create', method: 'POST', body }),
      invalidatesTags: ['Coupon'],
    }),
    couponManageList: builder.query<PageInfo<CouponCodeVO>, CouponQueryReq>({
      query: (body) => ({ url: '/m/coupon/list', method: 'POST', body: body ?? {} }),
      keepUnusedDataFor: 0,
    }),
    couponDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/m/coupon/delete', method: 'POST', body }),
      invalidatesTags: ['Coupon'],
    }),
    couponBatchDelete: builder.mutation<CouponBatchDeleteVO, CouponBatchDeleteReq>({
      query: (body) => ({ url: '/m/coupon/batch/delete', method: 'POST', body }),
      invalidatesTags: ['Coupon'],
    }),
  }),
});

// ---------- C 端：商品评论 ----------
export const itemCommentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    itemCommentList: builder.query<PageInfo<ItemCommentUserVO>, ItemCommentUserQueryReq>({
      query: (body) => ({ url: '/u/item/comment/list', method: 'POST', body }),
      keepUnusedDataFor: 0,
    }),
    itemCommentCreate: builder.mutation<string, ItemCommentUserCreateReq>({
      query: (body) => ({ url: '/u/item/comment/create', method: 'POST', body }),
    }),
  }),
});

// ---------- 管理端：商品评论 ----------
export const itemCommentManageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    itemCommentManageList: builder.query<PageInfo<ItemCommentManageVO>, ItemCommentQueryReq>({
      query: (body) => ({ url: '/m/item/comment/list', method: 'POST', body: body ?? {} }),
      keepUnusedDataFor: 0,
    }),
    itemCommentManageCreate: builder.mutation<string, ItemCommentCreateReq>({
      query: (body) => ({ url: '/m/item/comment/create', method: 'POST', body }),
    }),
    itemCommentReply: builder.mutation<string, ItemCommentCreateReq>({
      query: (body) => ({ url: '/m/item/comment/reply', method: 'POST', body }),
    }),
    itemCommentDelete: builder.mutation<void, BaseUuidReq>({
      query: (body) => ({ url: '/m/item/comment/delete', method: 'POST', body }),
    }),
  }),
});

// ---------- 导出 hooks（C 端） ----------
export const {
  useIndexCategoryQuery,
  useIndexSearchConditionQuery,
  useItemQueryQuery,
  useLazyItemQueryQuery,
  useLazyItemDetailQuery,
  useCountryGetQuery,
  useProvinceGetQuery,
  useLazyProvinceGetQuery,
} = retailApi;

export const {
  useCartGetQuery,
  useCartAddMutation,
  useCartUpdateMutation,
  useCartDeleteItemMutation,
  useCartDeleteSoldOutMutation,
} = cartApi;

export const {
  useOrderCreateCartMutation,
  useOrderCreateFromItemMutation,
  useOrderConfirmMutation,
  useCouponPreviewMutation,
  useLazyOrderListQuery,
  useLazyOrderDetailQuery,
  useOrderCancelMutation,
} = orderApi;

export const {
  useUserLoginMutation,
  useUserRegisterMutation,
  useUserVerifyMutation,
  useUserGetQuery,
  useLazyUserGetQuery,
  useUserLogoutMutation,
  useUserGuestMutation,
  useUserTokenShareConfirmMutation,
} = userApi;

export const {
  useAddressListQuery,
  useAddressAddMutation,
  useAddressUpdateMutation,
  useAddressDeleteMutation,
} = addressApi;

// ---------- 导出 hooks（管理端） ----------
export const {
  useCategoryAddMutation,
  useCategoryUpdateMutation,
  useCategoryDeleteMutation,
  useCategoryGetAllQuery,
} = categoryManageApi;

export const {
  useItemListQuery,
  useLazyItemListQuery,
  useItemCreateMutation,
  useItemUpdateMutation,
  useItemComplianceUpdateMutation,
  useItemStatusUpdateMutation,
  useItemDeleteMutation,
  useLazyItemManageDetailQuery,
  useLazyItemComplianceDetailQuery,
} = itemManageApi;

export const {
  useOrderLogisticsUpdateMutation,
  useLazyOrderManageListQuery,
  useLazyOrderManageDetailQuery,
  useOrderManageDeleteMutation,
} = orderManageApi;

export const {
  useCouponBatchCreateMutation,
  useLazyCouponManageListQuery,
  useCouponDeleteMutation,
  useCouponBatchDeleteMutation,
} = couponManageApi;

export const {
  useLazyItemCommentListQuery,
  useItemCommentCreateMutation,
} = itemCommentApi;

export const {
  useLazyItemCommentManageListQuery,
  useItemCommentManageCreateMutation,
  useItemCommentReplyMutation,
  useItemCommentDeleteMutation,
} = itemCommentManageApi;
